<template>
  <v-container fluid>
    <v-progress-linear v-if="loading" indeterminate color="deep-purple" />

    <v-row>
      <v-col cols="12" class="d-flex align-center justify-space-between flex-wrap">
        <div>
          <h2 class="deep-purple--text lighten-3">{{ $t('classlink.title') }}</h2>
          <p class="text-caption grey--text mb-0">{{ $t('classlink.subtitle') }}</p>
        </div>
        <v-btn
          v-if="!unavailable"
          color="deep-purple"
          dark
          small
          @click="openCreate"
        >
          <v-icon left small>mdi-plus</v-icon>
          {{ $t('classlink.new_connection') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-alert v-if="unavailable" type="info" outlined class="mt-4">
      <div class="font-weight-bold">{{ $t('classlink.unavailable.title') }}</div>
      <div>{{ $t('classlink.unavailable.text') }}</div>
    </v-alert>

    <v-card v-else class="pa-4 elevation-1 mt-2">
      <v-row dense class="mb-2">
        <v-col cols="12" sm="4">
          <v-text-field
            v-model="search"
            :label="$t('classlink.filters.search')"
            prepend-inner-icon="mdi-magnify"
            dense
            hide-details
            clearable
          />
        </v-col>
        <v-col cols="6" sm="3">
          <v-select
            v-model="statusFilter"
            :items="statusOptions"
            :label="$t('classlink.filters.status')"
            dense
            hide-details
          />
        </v-col>
        <v-col cols="6" sm="3">
          <v-select
            v-model="syncFilter"
            :items="syncOptions"
            :label="$t('classlink.filters.sync')"
            dense
            hide-details
          />
        </v-col>
        <v-col cols="12" sm="2" class="d-flex align-center">
          <v-checkbox
            v-model="missingSecretOnly"
            :label="$t('classlink.filters.missing_secret')"
            dense
            hide-details
            class="mt-0 pt-0"
          />
        </v-col>
      </v-row>
      <v-row v-if="hasActiveFilters" dense class="mb-2">
        <v-col cols="12" class="d-flex justify-end">
          <v-btn text small color="deep-purple" @click="clearFilters">
            {{ $t('classlink.filters.clear') }}
          </v-btn>
        </v-col>
      </v-row>

      <v-data-table
        :headers="headers"
        :items="filteredTenants"
        :loading="loading"
        :items-per-page="25"
        :no-data-text="noDataText"
        :loading-text="$t('classlink.loading')"
        :custom-sort="sortTenants"
        item-key="id"
        class="elevation-0"
        sort-by="displayName"
      >
        <template v-slot:[`item.displayName`]="{ item }">
          <span class="font-weight-medium">{{ item.displayName }}</span>
          <v-chip
            v-if="!item.hasSecret"
            x-small
            color="orange"
            dark
            class="ml-2"
          >
            {{ $t('classlink.missing_secret') }}
          </v-chip>
        </template>

        <template v-slot:[`item.tenantKey`]="{ item }">
          <code class="text-caption">{{ item.tenantKey }}</code>
        </template>

        <template v-slot:[`item.enabled`]="{ item }">
          <v-switch
            :input-value="item.enabled"
            :loading="isToggling(item.id)"
            :disabled="isToggling(item.id) || (!item.hasSecret && !item.enabled)"
            dense
            hide-details
            color="deep-purple"
            class="mt-0 pt-0"
            @change="toggleEnabled(item, $event)"
          />
        </template>

        <template v-slot:[`item.schools`]="{ item }">
          {{ count(item.stats.schools) }}
        </template>

        <template v-slot:[`item.courses`]="{ item }">
          {{ count(item.stats.courses) }}
        </template>

        <template v-slot:[`item.students`]="{ item }">
          {{ count(item.stats.students) }}
        </template>

        <template v-slot:[`item.teachers`]="{ item }">
          {{ count(item.stats.teachers) }}
        </template>

        <template v-slot:[`item.staff`]="{ item }">
          {{ count(item.stats.staff) }}
        </template>

        <template v-slot:[`item.source`]="{ item }">
          <div v-if="sourceOrgsText(item)" class="text-caption grey--text d-flex align-center">
            <div>
              <div>{{ sourceOrgsText(item) }}</div>
              <div>{{ sourceUsersText(item) }}</div>
            </div>
            <v-tooltip v-if="item.scopedSourceTotals" top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  small
                  color="deep-purple"
                  class="ml-1"
                  :aria-label="$t('classlink.stats.scoped_hint')"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-filter-outline
                </v-icon>
              </template>
              <span>{{ $t('classlink.stats.scoped_hint') }}</span>
            </v-tooltip>
          </div>
          <span v-else class="text-caption grey--text">—</span>
        </template>

        <template v-slot:[`item.lastSync`]="{ item }">
          <div>
            <div v-if="item.lastRun">
              <div class="d-flex align-center">
                <v-chip
                  :color="chipColor(item.lastRun)"
                  x-small
                  dark
                  class="mr-2"
                >
                  {{ outcomeLabel(item.lastRun) }}
                </v-chip>
                <span class="text-caption">{{ relative(item.lastRun.startedAt) }}</span>
              </div>
              <div class="text-caption grey--text">{{ countsText(item.lastRun) }}</div>
              <div v-if="attentionText(item)" class="text-caption amber--text text--darken-4">
                <v-icon x-small color="amber darken-4">mdi-alert</v-icon>
                {{ attentionText(item) }}
              </div>
              <div v-if="mismatchText(item)" class="text-caption red--text">
                <v-icon x-small color="red">mdi-alert-circle</v-icon>
                {{ mismatchText(item) }}
              </div>
            </div>
            <span v-else class="text-caption grey--text">{{ $t('classlink.run.never') }}</span>
          </div>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex align-center justify-end">
            <v-menu
              :value="isSyncMenuOpen(item.id)"
              offset-y
              @input="setSyncMenuOpen(item.id, $event)"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  text
                  small
                  color="deep-purple"
                  :disabled="!canSync(item)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon left small>mdi-sync</v-icon>
                  {{ $t('classlink.actions.sync') }}
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="selectSync(item, false)">
                  <v-list-item-title>{{ $t('classlink.actions.sync_incremental') }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="selectSync(item, true)">
                  <v-list-item-title>{{ $t('classlink.actions.sync_full') }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="ml-3"
                  :aria-label="$t('classlink.scope.open')"
                  :disabled="!canEditScope(item)"
                  v-bind="attrs"
                  v-on="on"
                  @click="openScope(item)"
                >
                  <v-icon small>mdi-filter-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('classlink.scope.open') }}</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="ml-3"
                  :aria-label="$t('classlink.reconcile.open')"
                  :disabled="!canSync(item)"
                  v-bind="attrs"
                  v-on="on"
                  @click="openUnlinked(item)"
                >
                  <v-icon small>mdi-link-off</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('classlink.reconcile.open') }}</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="ml-3"
                  :aria-label="$t('classlink.actions.history')"
                  v-bind="attrs"
                  v-on="on"
                  @click="openRuns(item)"
                >
                  <v-icon small>mdi-history</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('classlink.actions.history') }}</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="ml-3"
                  :aria-label="$t('classlink.actions.edit')"
                  v-bind="attrs"
                  v-on="on"
                  @click="openEdit(item)"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('classlink.actions.edit') }}</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="ml-3"
                  :aria-label="$t('classlink.actions.delete')"
                  v-bind="attrs"
                  v-on="on"
                  @click="confirmDelete(item)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('classlink.actions.delete') }}</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <class-link-tenant-form-modal
      v-model="formOpen"
      :tenant="editingTenant"
      @saved="loadTenants({ silent: true })"
    />

    <class-link-sync-runs-drawer
      v-model="runsOpen"
      :tenant="runsTenant"
    />

    <class-link-unlinked-orgs-drawer
      v-model="unlinkedOpen"
      :tenant="unlinkedTenant"
      @linked="loadTenants({ silent: true })"
    />

    <class-link-scope-drawer
      v-model="scopeOpen"
      :tenant="scopeTenant"
      @saved="loadTenants({ silent: true })"
    />

    <v-dialog v-model="deleteOpen" max-width="520">
      <v-card v-if="removingTenant">
        <v-card-title class="text-h6">
          {{ $t('classlink.delete.title') }}
        </v-card-title>
        <v-card-text>
          <i18n path="classlink.delete.confirm" tag="span">
            <template v-slot:name>
              <strong>{{ removingTenant.displayName }}</strong>
            </template>
            <template v-slot:tenantKey>
              <code>{{ removingTenant.tenantKey }}</code>
            </template>
            <template v-slot:users>
              <strong>
                {{ $t('classlink.delete.users_count', {
                  count: count(removingTenant.stats.mappedUsersCount),
                }) }}
              </strong>
            </template>
          </i18n>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="removing" @click="deleteOpen = false">
            {{ $t('classlink.delete.cancel') }}
          </v-btn>
          <v-btn color="red" dark :loading="removing" :disabled="removing" @click="removeTenant">
            {{ $t('classlink.delete.accept') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import rosterTenantsService from '@/services/roster-tenants.service';
import ClassLinkScopeDrawer from '@/components/classlink/ClassLinkScopeDrawer.vue';
import ClassLinkSyncRunsDrawer from '@/components/classlink/ClassLinkSyncRunsDrawer.vue';
import ClassLinkTenantFormModal from '@/components/classlink/ClassLinkTenantFormModal.vue';
import ClassLinkUnlinkedOrgsDrawer from '@/components/classlink/ClassLinkUnlinkedOrgsDrawer.vue';
import {
  POLL_INTERVAL_MS,
  apiErrorMessage,
  apiStatus,
  effectiveSourceTotals,
  formatCount,
  relativeTime,
  runChipColor,
  runCountsMessage,
  runOutcome,
  shouldPoll,
  sortTenants,
  tenantMismatch,
} from '@/utils/classlinkTenants';
import LoadingMixin from '@/mixins/loading';
import snackbarMixin from '@/mixins/snackbar';

export default {
  name: 'ClassLinkTenantsView',
  components: {
    ClassLinkScopeDrawer,
    ClassLinkSyncRunsDrawer,
    ClassLinkTenantFormModal,
    ClassLinkUnlinkedOrgsDrawer,
  },
  mixins: [LoadingMixin, snackbarMixin],
  data: () => ({
    tenants: [],
    unavailable: false,
    // Ids en vuelo del switch Habilitado. Objeto (no Set) porque Vue 2 no
    // reacciona a mutaciones de Set; se usa $set/$delete para que cada fila
    // reaccione de forma independiente cuando hay varios toggles en paralelo.
    togglingIds: {},
    // Estado abierto/cerrado del menú "Sincronizar" por fila (mismo patrón que
    // togglingIds). El v-menu por defecto cierra solo al elegir una opción,
    // pero al reusar filas por item-key="id" tras un reload (loadTenants)
    // podía quedar abierto y flotando sobre otro contenido (ej. el drawer de
    // historial); bindearlo a un estado propio lo hace explícito y confiable.
    syncMenuOpen: {},
    pollTimer: null,
    formOpen: false,
    editingTenant: null,
    runsOpen: false,
    runsTenant: null,
    unlinkedOpen: false,
    unlinkedTenant: null,
    scopeOpen: false,
    scopeTenant: null,
    deleteOpen: false,
    removingTenant: null,
    removing: false,
    search: '',
    statusFilter: 'all',
    syncFilter: 'all',
    missingSecretOnly: false,
  }),
  computed: {
    headers() {
      return [
        { text: this.$t('classlink.table.name'), value: 'displayName', align: 'start' },
        { text: this.$t('classlink.table.tenant_key'), value: 'tenantKey' },
        { text: this.$t('classlink.table.enabled'), value: 'enabled', sortable: false },
        {
          text: this.$t('classlink.table.schools'), value: 'schools', sortable: false, align: 'end',
        },
        {
          text: this.$t('classlink.table.courses'), value: 'courses', sortable: false, align: 'end',
        },
        {
          text: this.$t('classlink.table.students'), value: 'students', sortable: false, align: 'end',
        },
        {
          text: this.$t('classlink.table.teachers'), value: 'teachers', sortable: false, align: 'end',
        },
        {
          text: this.$t('classlink.table.staff'), value: 'staff', sortable: false, align: 'end',
        },
        {
          text: this.$t('classlink.table.source'), value: 'source', sortable: false, align: 'end',
        },
        { text: this.$t('classlink.table.last_sync'), value: 'lastSync', sortable: false },
        {
          text: this.$t('classlink.table.actions'), value: 'actions', sortable: false, align: 'end',
        },
      ];
    },
    statusOptions() {
      return [
        { text: this.$t('classlink.filters.status_all'), value: 'all' },
        { text: this.$t('classlink.filters.status_enabled'), value: 'enabled' },
        { text: this.$t('classlink.filters.status_disabled'), value: 'disabled' },
      ];
    },
    syncOptions() {
      return [
        { text: this.$t('classlink.filters.sync_all'), value: 'all' },
        { text: this.$t('classlink.status.running'), value: 'running' },
        { text: this.$t('classlink.outcome.applied'), value: 'applied' },
        { text: this.$t('classlink.outcome.no_changes'), value: 'no_changes' },
        { text: this.$t('classlink.outcome.needs_attention'), value: 'needs_attention' },
        { text: this.$t('classlink.filters.sync_never'), value: 'never' },
      ];
    },
    hasActiveFilters() {
      return !!this.search
        || this.statusFilter !== 'all'
        || this.syncFilter !== 'all'
        || this.missingSecretOnly;
    },
    noDataText() {
      return this.hasActiveFilters
        ? this.$t('classlink.filters.no_results')
        : this.$t('classlink.empty');
    },
    filteredTenants() {
      const search = this.search ? this.search.trim().toLowerCase() : '';
      return this.tenants.filter((tenant) => {
        if (search
          && !tenant.displayName.toLowerCase().includes(search)
          && !tenant.tenantKey.toLowerCase().includes(search)) {
          return false;
        }
        if (this.statusFilter === 'enabled' && !tenant.enabled) return false;
        if (this.statusFilter === 'disabled' && tenant.enabled) return false;
        if (this.syncFilter !== 'all') {
          const run = tenant.lastRun;
          if (this.syncFilter === 'never') {
            if (run) return false;
          } else if (this.syncFilter === 'running') {
            if (!run || run.status !== 'running') return false;
          } else if (!run || runOutcome(run) !== this.syncFilter) {
            return false;
          }
        }
        if (this.missingSecretOnly && tenant.hasSecret) return false;
        return true;
      });
    },
  },
  mounted() {
    this.loadTenants();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    sortTenants,
    clearFilters() {
      this.search = '';
      this.statusFilter = 'all';
      this.syncFilter = 'all';
      this.missingSecretOnly = false;
    },
    count(value) {
      return formatCount(value || 0, this.$i18n.locale);
    },
    relative(iso) {
      const message = relativeTime(iso);
      return message ? this.$t(message.key, message.params) : '';
    },
    chipColor(run) {
      return runChipColor(run);
    },
    // Mismo patrón `$te ? $t : fallback` que `stageLabel`/`outcomeLabel` y
    // `detailText`/`reasonText` del drawer: si aparece un outcome sin clave de
    // i18n todavía, se ve el código crudo y no la clave sin traducir.
    outcomeLabel(run) {
      const outcome = runOutcome(run);
      if (!outcome) return this.$t('classlink.status.running');
      const key = `classlink.outcome.${outcome}`;
      return this.$te(key) ? this.$t(key) : outcome;
    },
    // Todos los números de una misma fila pasan por `this.count()` (que los
    // agrupa: `2.497`, no `2497`): antes `countsText`/`mismatchText` los
    // mandaban crudos a `$t` mientras `statsTooltip` sí los agrupaba, y la
    // misma fila terminaba mostrando el mismo número con dos formatos.
    countsText(run) {
      const message = runCountsMessage(run);
      if (!message) return '';
      const {
        users, courses, skipped, ambiguous, errors,
      } = message.params;
      return this.$t(message.key, {
        users: this.count(users),
        courses: this.count(courses),
        skipped: this.count(skipped),
        ambiguous: this.count(ambiguous),
        errors: this.count(errors),
      });
    },
    // Una incremental sin cambios no debe tapar una corrida completa que falló
    // el día anterior: si hay una corrida con problemas anterior a la última,
    // se dice en la fila.
    attentionText(tenant) {
      const attention = tenant.lastAttentionRun;
      if (!attention) return '';
      if (tenant.lastRun && attention.id === tenant.lastRun.id) return '';
      const message = relativeTime(attention.startedAt);
      return message
        ? this.$t('classlink.last_attention', { when: this.$t(message.key, message.params) })
        : '';
    },
    mismatchText(tenant) {
      const message = tenantMismatch(tenant);
      if (!message) return '';
      return this.$t(message.key, { users: this.count(message.params.users) });
    },
    // Lo que ClassLink dice que tiene, en su propia columna al lado de los
    // números de BeeReaders. Esa comparación es lo que hace que un distrito que
    // no sincronizó se lea de un vistazo: "5.065 usuarios" enfrentado a un "0".
    sourceOrgsText(tenant) {
      const totals = effectiveSourceTotals(tenant);
      if (!totals) return '';
      return this.$tc('classlink.stats.source_orgs', totals.orgs, {
        n: this.count(totals.orgs),
      });
    },
    sourceUsersText(tenant) {
      const totals = effectiveSourceTotals(tenant);
      if (!totals) return '';
      return this.$tc('classlink.stats.source_users', totals.users, {
        n: this.count(totals.users),
      });
    },
    isToggling(id) {
      return !!this.togglingIds[id];
    },
    canSync(tenant) {
      return tenant.enabled
        && tenant.hasSecret
        && !(tenant.lastRun && tenant.lastRun.status === 'running');
    },
    async loadTenants({ silent = false } = {}) {
      if (!silent) this.loader();
      try {
        this.tenants = await rosterTenantsService.list();
        this.unavailable = false;
      } catch (error) {
        // 404 = el backend de KB-939 todavía no está desplegado en este
        // ambiente (el PR del admin-ui puede mergear antes que el del backend).
        if (apiStatus(error) === 404) {
          this.unavailable = true;
          this.tenants = [];
        } else if (!silent) {
          this.snackbarError(apiErrorMessage(error, this.$t('classlink.load_error')));
        }
      } finally {
        if (!silent) this.loader();
        this.syncPolling();
      }
    },
    // Polling cada 5 s mientras alguna conexión tenga una corrida `running`;
    // se apaga solo en cuanto no queda ninguna (y en beforeDestroy).
    syncPolling() {
      if (!this.unavailable && shouldPoll(this.tenants)) {
        this.startPolling();
      } else {
        this.stopPolling();
      }
    },
    startPolling() {
      if (this.pollTimer) return;
      this.pollTimer = window.setInterval(() => {
        this.loadTenants({ silent: true });
      }, POLL_INTERVAL_MS);
    },
    stopPolling() {
      if (!this.pollTimer) return;
      window.clearInterval(this.pollTimer);
      this.pollTimer = null;
    },
    async toggleEnabled(tenant, enabled) {
      this.$set(this.togglingIds, tenant.id, true);
      // Optimista: si el PATCH es rechazado (ej. 422 por habilitar sin
      // secret), el recargo de abajo trae el valor real del servidor
      // (distinto del que se puso acá), y eso es lo que hace que el switch
      // -reutilizado por item-key="id"- vuelva a su estado correcto.
      this.$set(tenant, 'enabled', enabled);
      try {
        await rosterTenantsService.setEnabled(tenant.id, enabled);
        this.snackbarSuccess(this.$t(enabled ? 'classlink.enabled_switch.on' : 'classlink.enabled_switch.off'));
      } catch (error) {
        // 422 = habilitar una conexión sin client secret. El backend manda un
        // mensaje concreto; se muestra tal cual.
        this.snackbarError(apiErrorMessage(error, this.$t('general.error_msg')));
      } finally {
        // Cada request limpia solo su propio id: togglear dos filas en
        // paralelo no debe hacer que la primera pise el estado de la segunda.
        this.$delete(this.togglingIds, tenant.id);
        // Recarga siempre: si el PATCH falló, devuelve el switch a su valor real.
        await this.loadTenants({ silent: true });
      }
    },
    isSyncMenuOpen(id) {
      return !!this.syncMenuOpen[id];
    },
    setSyncMenuOpen(id, open) {
      if (open) {
        this.$set(this.syncMenuOpen, id, true);
      } else {
        this.$delete(this.syncMenuOpen, id);
      }
    },
    // Handler de los v-list-item del menú "Sincronizar": cierra el menú de esa
    // fila de forma explícita antes de disparar la corrida.
    selectSync(tenant, fullSync) {
      this.setSyncMenuOpen(tenant.id, false);
      this.runSync(tenant, fullSync);
    },
    async runSync(tenant, fullSync) {
      try {
        await rosterTenantsService.triggerSync(tenant.id, fullSync);
        this.snackbarSuccess(this.$t(fullSync ? 'classlink.sync.started_full' : 'classlink.sync.started_incremental'));
      } catch (error) {
        // 409 = ya hay una corrida en curso; 422 = deshabilitada o sin secret.
        this.snackbarError(apiErrorMessage(error, this.$t('general.error_msg')));
      } finally {
        await this.loadTenants({ silent: true });
      }
    },
    openCreate() {
      this.editingTenant = null;
      this.formOpen = true;
    },
    openEdit(tenant) {
      this.editingTenant = tenant;
      this.formOpen = true;
    },
    openRuns(tenant) {
      // Por las dudas: si el menú "Sincronizar" de alguna fila quedó abierto,
      // que no quede flotando por encima del drawer de historial.
      this.syncMenuOpen = {};
      this.runsTenant = tenant;
      this.runsOpen = true;
    },
    openUnlinked(tenant) {
      // Mismo motivo que en `openRuns`.
      this.syncMenuOpen = {};
      this.unlinkedTenant = tenant;
      this.unlinkedOpen = true;
    },
    /**
     * Gate propio y no `canSync`: el alcance tiene que poder configurarse
     * ANTES de la primera sincronización (crear la conexión, elegir escuelas,
     * recién ahí habilitar y sincronizar), así que ni `enabled` ni "no hay
     * una corrida en curso" corresponden acá. Lo único que hace falta es el
     * client secret, porque listar las orgs consulta a ClassLink en vivo.
     */
    canEditScope(tenant) {
      return tenant.hasSecret;
    },
    openScope(tenant) {
      // Mismo motivo que en `openRuns`.
      this.syncMenuOpen = {};
      this.scopeTenant = tenant;
      this.scopeOpen = true;
    },
    confirmDelete(tenant) {
      this.removingTenant = tenant;
      this.deleteOpen = true;
    },
    async removeTenant() {
      this.removing = true;
      try {
        await rosterTenantsService.remove(this.removingTenant.id);
        this.snackbarSuccess(this.$t('classlink.delete.deleted'));
        this.deleteOpen = false;
      } catch (error) {
        // 409 = la conexión tiene una corrida en curso. El backend manda el
        // mensaje ("Hay una sincronización en curso; espera a que termine
        // antes de eliminar") y se muestra tal cual.
        this.snackbarError(apiErrorMessage(error, this.$t('general.error_msg')));
      } finally {
        this.removing = false;
        await this.loadTenants({ silent: true });
      }
    },
  },
};
</script>

<style scoped>
</style>
